import Axios from 'axios'

import { extractByTypename, getQaPrLink, ThumbnailRatio } from '@local/utils'

import { ImageItem, VideoItem } from '@web/gql/graphql'
import { IS_PR_QA_MODE, PR_NUMBER } from '@web/libs/constants'
import { Nullish } from '@web/types'

import links from '../constants/links'
import { neloLogger } from '../utils'
import photoInfra from './photoInfra'
import { ThumbnailType } from './photoInfra.constant'

Axios.defaults.withCredentials = true

type PartialImageItem = Pick<ImageItem, '__typename' | 'url'>
type PartialVideoItem = Pick<VideoItem, '__typename' | 'thumbnail'>

/**
 * 미디어 아이템으로부터 썸네일 URL를 가져옵니다.
 * @param mediaItem url, thumbnail 필드를 가지고 있는 미디어 아이템
 * @returns 썸네일 URL 주소
 */
export const getThumbnail = (mediaItem: Nullish<PartialImageItem | PartialVideoItem>): Nullish<string> => {
  if (mediaItem?.__typename === 'ImageItem') {
    return mediaItem.url
  }

  if (mediaItem?.__typename === 'VideoItem') {
    return mediaItem.thumbnail
  }

  return null
}

type UploadProfileImageResponse =
  | { success: true; imageUrl: string; originImageUrl: string }
  | { success: false; message: 'noAuthorized' | 'photoCloudUploadFailed' }
  | { success: false; message: 'unacceptableImage'; imageUrl: string; originImageUrl: string }

export const uploadProfileImage = async (file: File) => {
  const form = new FormData()
  form.append('file', file)

  const url = `${IS_PR_QA_MODE ? getQaPrLink(PR_NUMBER).MYPLACE_API : links.FILE_UPLOAD}`
  return await Axios.post<UploadProfileImageResponse>(url, form).then(({ data }) => data)
}

export const uploadImageSimple = async (file: File) => {
  const form = new FormData()
  form.append('file', file)
  const url = `${links.FILE_UPLOAD}/simple`
  const result = await Axios.post<{ imageUrl: string; originImageUrl: string }>(url, form)
    .then(({ data }) => data)
    .catch(error => {
      neloLogger('error', { on: 'uploadImageSimple', error }).catch(null)
      return null
    })
  return result
}

export const getThumbnailTypeByRatio = ({
  ratio,
  fixedBy = 'width',
}: {
  ratio: ThumbnailRatio
  fixedBy?: 'width' | 'height'
}): ThumbnailType => {
  const map = {
    width: {
      [ThumbnailRatio.PORTRAIT]: photoInfra.THUMBNAIL_TYPES.myplace.f912_1140,
      [ThumbnailRatio.LANDSCAPE]: photoInfra.THUMBNAIL_TYPES.myplace.f912_608,
      [ThumbnailRatio.SQUARE]: photoInfra.THUMBNAIL_TYPES.myplace.f912_912,
    },
    height: {
      [ThumbnailRatio.PORTRAIT]: photoInfra.THUMBNAIL_TYPES.myplace.f352_440,
      [ThumbnailRatio.LANDSCAPE]: photoInfra.THUMBNAIL_TYPES.myplace.f660_440,
      [ThumbnailRatio.SQUARE]: photoInfra.THUMBNAIL_TYPES.myplace.f440_440,
    },
  } as const

  return map[fixedBy][ratio] ?? map.width[ThumbnailRatio.SQUARE]
}

type TargetMediaItem =
  | { __typename?: 'ImageItem'; url: string }
  | { __typename?: 'VideoItem'; thumbnail: string }
  | null
  | undefined

export const getImageUrlFromMediaItem = (mediaItem: TargetMediaItem) => {
  const { ImageItem, VideoItem } = extractByTypename(mediaItem)
  return photoInfra.convertThumbnail({
    url: ImageItem?.url ?? VideoItem?.thumbnail,
    type: photoInfra.type.f220_220_70_sharpen,
  })
}
